import React, { useContext, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';
import color, { fox } from 'styles/colors';
import { fadein } from 'styles/animations';
import { respondTo } from 'styles/helper';
import { Profile } from 'lib/types/profileType';
import { useFlowManager } from 'lib/hooks';
import { getRegCodeFromPath, sanitizeRegCode } from 'lib/utils/regCode';
import RegCodeSchema from 'validations/schemas/RegCodeSchema';
import { AppContext } from 'context/GlobalContextProvider';
import { Button, Input } from 'components/common';
import useRegCodeInput from './hooks';

type Props = {
  setRegCodeError: (b: boolean) => boolean | void;
  profile: Profile;
};

type IFormInput = {
  regCode: string;
};

const RegCodeInput = ({ profile, setRegCodeError }: Props): JSX.Element => {
  const {
    state: { error },
  } = useContext(AppContext);
  const { handleSubmit: handleSubmitRegCode } = useRegCodeInput({
    profile,
    setRegCodeError,
  });
  const { isNation } = useFlowManager();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<IFormInput>({
    mode: 'onChange',
    reValidateMode: 'onSubmit',
    resolver: RegCodeSchema(),
    defaultValues: { regCode: '' },
  });

  useEffect(() => {
    const code = getRegCodeFromPath(window.location.pathname);
    if (code) setValue('regCode', code, { shouldValidate: true });
  }, [setValue]);

  const onSubmit: SubmitHandler<IFormInput> = async (data: IFormInput) => {
    await handleSubmitRegCode(sanitizeRegCode(data.regCode));
  };

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="regCode"
          control={control}
          render={({ field: { name, value, onBlur, onChange, ref } }) => (
            <Input
              ref={ref}
              name={name}
              value={value}
              onBlur={onBlur}
              onChange={(event) => onChange(sanitizeRegCode(event.target.value))}
              placeholder="Activation Code"
              aria-label="Activation Code"
              aria-invalid={!!error}
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              type="text"
              autoFocus
              className={error ? 'error' : ''}
            />
          )}
        />
        <Button kind={isNation ? 'secondary' : 'default'} type="submit" disabled={!isValid}>
          Submit
        </Button>
      </Form>
    </>
  );
};

export default RegCodeInput;

const Form = styled.form`
  ${fadein};
  input {
    text-transform: uppercase;
    padding: 0;
    ${respondTo.bp_768`
      width: 200px;
  `}
    &:focus,
    :active {
      border: solid 2px ${fox.azureRadiance};
    }
    &::placeholder {
      color: ${color.white};
      text-transform: initial;
      opacity: 0.5;
    }
    &.error {
      border: solid 2px ${color.error};
    }
  }
`;
